import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, MenuItem, TextField, Autocomplete, Alert,
  CircularProgress, Chip, InputAdornment,
} from '@mui/material';
import { SaveOutlined, SendOutlined } from '@mui/icons-material';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill, WorkspaceHeader, ValidationStrip,
} from '../../components/common/workspaceKit';
import { formatIndianNumber } from '../../utils/currency';
import { hrService, EmployeeListItem, Department } from '../../services/hrService';
import { procurementService, Vendor } from '../../services/procurementService';
import { projectService, Project } from '../../services/projectService';
import { budgetService, BudgetCheck } from '../../services/budgetService';
import { useTaxCalculation } from '../../hooks/useTaxCalculation';
import { TaxSummaryPanel } from '../../components/common/TaxSummaryPanel';
import { financeService } from '../../services/financeService';
import { expenseService, ExpenseCategory } from '../../services/expenseService';
import AccountSelect from '../../components/finance/AccountSelect';
import { api } from '../../services/apiClient';
import { PaidBy, PAID_BY_OPTIONS, PaymentMethod, toNum } from './ExpensePage';

interface CostCenter { id: number; code: string; name: string; isActive?: boolean }
const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'BANK', 'CARD', 'UPI', 'PETTY_CASH'];
const todayISO = () => new Date().toISOString().slice(0, 10);

const SectionCard: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: P.strong }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: '12px', color: P.muted, mt: '2px' }}>{sub}</Typography>}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);
const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: '16px' } as const;

const ExpenseFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loadingRef, setLoadingRef] = useState(true);
  const [loadingExpense, setLoadingExpense] = useState(isEdit);

  const [expenseDate, setExpenseDate] = useState(todayISO());
  const [postingDate, setPostingDate] = useState('');
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('BANK');
  const [paidBy, setPaidBy] = useState<PaidBy>('COMPANY');
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [remarks, setRemarks] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'' | 'draft' | 'submit'>('');
  const [budgetChk, setBudgetChk] = useState<BudgetCheck | null>(null);

  const selectedCategory = useMemo(() => categories.find((c) => String(c.id) === categoryId) || null, [categories, categoryId]);
  const canEdit = !isEdit || status === 'DRAFT';

  // Reference data.
  useEffect(() => {
    setLoadingRef(true);
    Promise.all([
      hrService.getEmployees({ limit: 500 }).catch(() => ({ employees: [], pagination: { total: 0, page: 1, limit: 0, totalPages: 0 } } as any)),
      hrService.getDepartments().catch(() => ({ departments: [], count: 0 })),
      api.get<{ costCenters: CostCenter[] }>('/cost-centers').catch(() => ({ costCenters: [] as CostCenter[] })),
      projectService.getProjects().catch(() => [] as Project[]),
      procurementService.getVendors().catch(() => ({ vendors: [], count: 0 })),
      expenseService.listCategories(true).catch(() => ({ categories: [], count: 0 })),
      financeService.getAccounts({ isActive: true }).catch(() => ({ accounts: [], stats: null as any, count: 0 })),
    ]).then(([emp, dep, cc, prj, ven, cat, acc]: any) => {
      setEmployees(emp.employees || []);
      setDepartments(dep.departments || []);
      setCostCenters((cc.costCenters || []).filter((c: CostCenter) => c.isActive !== false));
      setProjects(prj || []);
      setVendors((ven.vendors || []).filter((v: Vendor) => v.is_active !== false));
      setCategories(cat.categories || []);
      setAccounts(acc.accounts || []);
    }).finally(() => setLoadingRef(false));
  }, []);

  // Preload the expense once ref data is present (edit mode).
  useEffect(() => {
    if (!isEdit || !id || loadingRef) return;
    setLoadingExpense(true);
    expenseService.get(id).then(({ expense }) => {
      setExpenseDate(expense.expense_date ? String(expense.expense_date).slice(0, 10) : todayISO());
      setPostingDate(expense.posting_date ? String(expense.posting_date).slice(0, 10) : '');
      setEmployee(employees.find((e) => String(e.id) === String(expense.employee_id)) || null);
      setCategoryId(expense.category_id ? String(expense.category_id) : '');
      setDescription(expense.description || '');
      setAmount(String(expense.amount ?? ''));
      // The tax is not loaded into an editable field — it is re-determined
      // from the masters as soon as the amount and category are in place.
      setCostCenterId(expense.cost_center_id ? String(expense.cost_center_id) : '');
      setProjectId(expense.project_id ? String(expense.project_id) : '');
      setDepartmentId(expense.department_id ? String(expense.department_id) : '');
      setGlAccountId(expense.gl_account_id ? String(expense.gl_account_id) : '');
      setPaymentMethod((expense.payment_method as PaymentMethod) || 'BANK');
      setPaidBy((expense.paid_by as PaidBy) || 'COMPANY');
      setVendor(expense.vendor_id ? vendors.find((v) => String(v.id) === String(expense.vendor_id)) || null : null);
      setRemarks(expense.remarks || '');
      setStatus(expense.status);
    }).catch((e: any) => setError(e.message || 'Failed to load expense')).finally(() => setLoadingExpense(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRef]);

  // Selecting an employee derives their department + cost center (overridable).
  const onEmployee = (v: EmployeeListItem | null) => {
    setEmployee(v);
    if (v) {
      if (v.department_id) setDepartmentId(String(v.department_id));
      const cc = (v as any).cost_center_id;
      if (cc) setCostCenterId(String(cc));
    }
  };

  // Selecting a category pre-fills its default GL account.
  const onCategory = (cid: string) => {
    setCategoryId(cid);
    const cat = categories.find((c) => String(c.id) === cid);
    if (cat && cat.default_gl_account_id) setGlAccountId(String(cat.default_gl_account_id));
  };

  // Tax is DETERMINED, not typed.
  //
  // This form used to render a free-text "Tax" box in rupees with an "apply"
  // link that multiplied the category's default rate out once. That made the
  // GST on an expense a snapshot of a calculator: editing the amount afterwards
  // left a tax figure that no longer related to it, nothing recorded the
  // CGST/SGST/IGST split or the SAC, and reverse charge — which is the normal
  // case for freight, legal fees and security services — could not be
  // expressed at all.
  const expenseTaxInput = useMemo(() => {
    if (!categoryId || toNum(amount) <= 0) return null;
    return {
      transactionType: 'EXPENSE' as const,
      partyId: vendor?.id ? Number(vendor.id) : null,
      documentDate: expenseDate,
      lines: [{
        taxCategoryId: selectedCategory?.default_tax_category_id ?? null,
        hsnCode: selectedCategory?.default_sac_code ?? null,
        quantity: 1,
        unitPrice: toNum(amount),
        description: description.trim() || selectedCategory?.name || 'Expense',
      }],
    };
  }, [categoryId, amount, vendor?.id, expenseDate, description, selectedCategory]);

  const { tax: expenseTax, loading: taxLoading, error: taxError } = useTaxCalculation(expenseTaxInput);

  const taxAmount = expenseTax ? expenseTax.totalTax : 0;
  const total = expenseTax ? expenseTax.totalAmount : toNum(amount);

  // Advisory budget check (non-blocking) — unchanged behaviour.
  useEffect(() => {
    const cc = costCenterId ? Number(costCenterId) : undefined;
    const pj = projectId ? Number(projectId) : undefined;
    const amt = toNum(amount);
    if ((!cc && !pj) || amt <= 0 || !expenseDate) { setBudgetChk(null); return undefined; }
    const t = setTimeout(() => {
      budgetService.check({ date: expenseDate, amount: amt, costCenterId: cc, projectId: pj }).then(setBudgetChk).catch(() => setBudgetChk(null));
    }, 400);
    return () => clearTimeout(t);
  }, [costCenterId, projectId, amount, expenseDate]);

  const checks = [
    { ok: !!employee, label: employee ? 'Employee selected' : 'Employee required' },
    { ok: !!categoryId, label: categoryId ? 'Category set' : 'Category required' },
    { ok: toNum(amount) > 0, label: toNum(amount) > 0 ? `Amount ${formatIndianNumber(toNum(amount))}` : 'Amount required' },
    { ok: !!glAccountId, label: glAccountId ? 'GL account set' : 'GL account (from category or pick one)' },
    ...(selectedCategory && (selectedCategory.receipt_required === 1 || selectedCategory.receipt_required === true)
      ? [{ ok: false, label: 'Receipt required for this category' }] : []),
  ];
  const canSave = !!employee && !!categoryId && toNum(amount) > 0 && !saving;

  const buildPayload = () => ({
    expenseDate,
    postingDate: postingDate || undefined,
    employeeId: employee?.id ? Number(employee.id) : undefined,
    categoryId: categoryId ? Number(categoryId) : undefined,
    category: selectedCategory?.name || undefined, // keep legacy text for summaries
    description: description.trim() || undefined,
    amount: toNum(amount),
    // No tax is sent: the server re-determines it on save from the category's
    // tax category, the vendor's state and the reverse-charge rules, so the
    // browser has nothing to get wrong and nothing to tamper with.
    taxCategoryId: selectedCategory?.default_tax_category_id ?? undefined,
    costCenterId: costCenterId ? Number(costCenterId) : undefined,
    projectId: projectId ? Number(projectId) : undefined,
    departmentId: departmentId ? Number(departmentId) : undefined,
    glAccountId: glAccountId ? Number(glAccountId) : undefined,
    paymentMethod,
    paidBy,
    vendorId: vendor?.id ? Number(vendor.id) : undefined,
    remarks: remarks.trim() || undefined,
  });

  const save = async (thenSubmit: boolean) => {
    setSaving(thenSubmit ? 'submit' : 'draft'); setError(null);
    try {
      const payload = buildPayload();
      const res = isEdit && id ? await expenseService.update(id, payload) : await expenseService.create(payload);
      const newId = res.expense.id;
      if (thenSubmit) await expenseService.submit(newId);
      navigate(`/finance/expenses/${newId}`);
    } catch (e: any) { setError(e.message || 'Failed to save expense'); setSaving(''); }
  };

  const loading = loadingRef || loadingExpense;

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 6, color: P.text }}>
      <WorkspaceHeader
        onBack={() => navigate(isEdit && id ? `/finance/expenses/${id}` : '/finance/expenses')}
        title={isEdit ? 'Edit Expense' : 'New Expense'}
        subtitle="Employee spend → validation → approval → accounting → settlement."
        chip={<StatusPill label={status || 'DRAFT'} tone={status === 'PAID' ? 'green' : status === 'REJECTED' || status === 'CANCELLED' ? 'red' : 'draft'} />}
        actions={canEdit ? (
          <>
            <Button variant="outlined" sx={outBtn} onClick={() => navigate('/finance/expenses')} disabled={!!saving}>Cancel</Button>
            <Button variant="outlined" sx={outBtn} onClick={() => save(false)} disabled={!canSave}
              startIcon={saving === 'draft' ? <CircularProgress size={16} /> : <SaveOutlined sx={{ fontSize: 18 }} />}>Save Draft</Button>
            <Button variant="contained" sx={priBtn} onClick={() => save(true)} disabled={!canSave}
              startIcon={saving === 'submit' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendOutlined sx={{ fontSize: 18 }} />}>Submit for Approval</Button>
          </>
        ) : undefined}
      />
      <ValidationStrip items={checks} />

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ px: PX, pt: 1.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {!canEdit && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>This expense is {status} and can no longer be edited.</Alert>}
          {budgetChk && budgetChk.hasBudget && (
            <Alert severity={budgetChk.exceeds ? 'warning' : 'info'} sx={{ mb: 2, borderRadius: 2 }}>
              {budgetChk.exceeds ? `This spend would exceed the available budget by ${formatIndianNumber(budgetChk.overBy)}. ` : 'Within available budget. '}
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Budget {formatIndianNumber(budgetChk.budget)} · Actual {formatIndianNumber(budgetChk.actual)}
                {budgetChk.committed > 0 ? ` · Committed ${formatIndianNumber(budgetChk.committed)}` : ''} · Available {formatIndianNumber(budgetChk.available)}
              </Box>
            </Alert>
          )}

          <SectionCard title="Expense Basics">
            <Box sx={grid}>
              <Box><FieldLabel text="Company / Legal Entity" /><TextField fullWidth size="small" value="Current Company (tenant)" disabled sx={fieldSx} /></Box>
              <Box><FieldLabel text="Employee" required /><Autocomplete size="small" options={employees} value={employee} onChange={(_, v) => onEmployee(v)} disabled={!canEdit}
                getOptionLabel={(o) => `${o.employee_code} — ${o.first_name}${o.last_name ? ` ${o.last_name}` : ''}`} isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                renderInput={(pr) => <TextField {...pr} placeholder="Search employee…" sx={fieldSx} />} /></Box>
              <Box><FieldLabel text="Expense Date" required /><TextField type="date" fullWidth size="small" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} disabled={!canEdit} sx={fieldSx} /></Box>
              <Box><FieldLabel text="Category" required /><TextField select fullWidth size="small" value={categoryId} onChange={(e) => onCategory(e.target.value)} disabled={!canEdit} sx={fieldSx}
                helperText={selectedCategory?.default_gl_account_code ? `Default GL ${selectedCategory.default_gl_account_code}` : (categories.length ? ' ' : 'No categories — create some in the master')}>
                <MenuItem value="">— Select —</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.code} · {c.name}</MenuItem>)}
              </TextField></Box>
              <Box><FieldLabel text="Posting Date" /><TextField type="date" fullWidth size="small" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} disabled={!canEdit} sx={fieldSx} helperText="Defaults to expense date" /></Box>
              <Box sx={{ gridColumn: { xs: '1', md: 'span 3' } }}><FieldLabel text="Business Purpose / Description" /><TextField fullWidth size="small" value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit} placeholder="Why was this incurred?" sx={fieldSx} /></Box>
            </Box>
          </SectionCard>

          <SectionCard
            title="Amount & Tax"
            sub="Enter the amount before tax — GST is worked out from the category's tax classification and the supplier's state."
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 380px' }, gap: '16px', alignItems: 'start' }}>
              <Box><FieldLabel text="Amount (before tax)" required /><TextField type="number" fullWidth size="small" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={!canEdit} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={fieldSx} /></Box>
              <Box><FieldLabel text="Total (incl. tax)" /><Box sx={{ height: 42, display: 'flex', alignItems: 'center', px: 1.5, borderRadius: '10px', border: `1px solid ${P.line}`, bgcolor: P.head }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: P.strong }}>{formatIndianNumber(total)}</Typography></Box>
                <Typography sx={{ fontSize: '11.5px', color: P.muted, mt: '6px' }}>
                  {expenseTax
                    ? `Tax ${formatIndianNumber(taxAmount)} determined by the system`
                    : selectedCategory
                      ? 'Enter an amount to see the tax'
                      : 'Pick a category — it carries the tax classification'}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: { xs: '1', md: '3' }, gridRow: { md: '1 / span 1' } }}>
                <TaxSummaryPanel tax={expenseTax} loading={taxLoading} error={taxError} title="Tax breakup" />
              </Box>
            </Box>
          </SectionCard>

          <SectionCard title="Accounting & Dimensions" sub="GL account and dimensions come from the masters — no free text.">
            <Box sx={grid}>
              <Box><FieldLabel text="GL Account" /><AccountSelect accounts={accounts} value={glAccountId} onChange={setGlAccountId} placeholder="Search account…" /></Box>
              <Box><FieldLabel text="Cost Center" /><TextField select fullWidth size="small" value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>{costCenters.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.code} — {c.name}</MenuItem>)}
              </TextField></Box>
              <Box><FieldLabel text="Project" /><TextField select fullWidth size="small" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>{projects.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.project_code} — {p.project_name}</MenuItem>)}
              </TextField></Box>
              <Box><FieldLabel text="Department" /><TextField select fullWidth size="small" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} disabled={!canEdit} sx={fieldSx} helperText="From Department master">
                <MenuItem value="">Not assigned</MenuItem>{departments.map((d) => <MenuItem key={d.id} value={String(d.id)}>{d.dept_code} — {d.dept_name}</MenuItem>)}
              </TextField></Box>
            </Box>
          </SectionCard>

          <SectionCard title="Settlement">
            <Box sx={grid}>
              <Box><FieldLabel text="Settlement (Paid By)" /><TextField select fullWidth size="small" value={paidBy} onChange={(e) => setPaidBy(e.target.value as PaidBy)} disabled={!canEdit} sx={fieldSx}>
                {PAID_BY_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
                <Typography sx={{ fontSize: '11.5px', color: P.muted, mt: 0.5 }}>{paidBy === 'EMPLOYEE_REIMBURSEMENT' ? 'Credits Accounts Payable to the employee until reimbursed.' : 'Credits company bank/cash directly.'}</Typography></Box>
              <Box><FieldLabel text="Payment Method" /><TextField select fullWidth size="small" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)} disabled={!canEdit} sx={fieldSx}>
                {PAYMENT_METHODS.map((m) => <MenuItem key={m} value={m}>{m.replace(/_/g, ' ')}</MenuItem>)}
              </TextField></Box>
              <Box><FieldLabel text="Vendor (optional)" /><Autocomplete size="small" options={vendors} value={vendor} onChange={(_, v) => setVendor(v)} disabled={!canEdit}
                getOptionLabel={(o) => `${o.company_name} (${o.vendor_code})`} isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                renderInput={(pr) => <TextField {...pr} placeholder="Search vendor…" sx={fieldSx} />} /></Box>
              <Box sx={{ gridColumn: { xs: '1', md: 'span 3' } }}><FieldLabel text="Remarks" /><TextField fullWidth size="small" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canEdit} sx={fieldSx} /></Box>
            </Box>
            {selectedCategory && (selectedCategory.receipt_required === 1 || selectedCategory.receipt_required === true) && (
              <Chip size="small" label="Receipt required for this category" sx={{ mt: 1.5, bgcolor: P.amberBg, color: P.amber, fontWeight: 700 }} />
            )}
          </SectionCard>
        </Box>
      )}
    </Box>
  );
};

export default ExpenseFormPage;
